import StatusTag from "components/StatusTag";
import EllipsisTooltipCell from 'utils/ellipsisTooltipCell';

export const defaultIsLoading = {
  wireless: false,
  updateWireless: false,
  radius: false,
  wirelessSchedule: false,
  macFilter: false,
  ipFilter: false,
  captivePortal: false,
}

export const defaultAlertInfo = {
  open: false,
  severity: "",
  message: "",
};

export const defaultBandOptions = {
  2: "2.4",
  5: "5",
  6: "6",
};

export const getColumnsData = ({
  columnHelper,
}) => {
  return [
    columnHelper.accessor("enable", {
      header: "Status",
      cell: (info) => {
        const statusMapping = {
          green: "true",
          grey: "false",
        };

        const labelMapping = {
          true: "Active",
          false: "Inactive",
        };

        return (
          <StatusTag
            status={info.getValue()}
            statusMapping={statusMapping}
            labelMapping={labelMapping}
          />
        );
      },
    }),
    columnHelper.accessor("ssid", {
      header: "Wi-Fi Name",
      cell: (info) => {
        const value = info.getValue();
        return <EllipsisTooltipCell text={value ?? "-"} />;
      },
    }),
    columnHelper.accessor("vlanId", {
      header: "VLAN",
    }),
    columnHelper.accessor("band", {
      header: "Wi-Fi Band",
      cell: (info) => {
        const bands = info.getValue();

        if (!bands) return "-";
        if (Array.isArray(bands))
          return bands.map((b) => `${defaultBandOptions[b]}GHz`).join(" / ");
        return `${bands}GHz`;
      },
    }),
    columnHelper.accessor("security", {
      header: "Security",
    }),
    columnHelper.accessor("clients", {
      header: "Clients",
    }),
  ];
}

export const defaultFormData = {
  ssid: null,
  band: [],
  mloEnable: false,
  security: '',
  pmfState: 'Disable',
  wpaMode: '',
  encryption: '',
  password: null,
  vlanId: null,
  applyTo: {
    type: 'all',
    targets: [],
  },
  broadcastSSID: true,
  enable: true,
  groupKeyUpdatePeriod: 3600,
  fastRoaming: false,
  clientIsolation: false,
  rateLimit: 'DISABLE',
  rateLimitDownload: null,
  rateLimitUpload: null,
  wirelessSchedule: 'disabled',
  macFilter: 'disabled',
  ipFilter: 'disabled',
  captivePortalName: 'disabled',
  radiusProfile: null,
};

export const securityTypeAssembleList = [
  {
    label: 'Open',
    value: 'OPEN',
    bandSupportList: ['2', '5'],
    isShow: true,
  },
  {
    label: 'Enhance Open',
    value: 'ENHANCE_OPEN',
    bandSupportList: ['2', '5', '6'],
    isShow: true,
  },
  {
    label: 'Enhance Open+Open',
    value: 'ENHANCE_OPEN+OPEN',
    bandSupportList: ['2', '5'],
    isShow: true,
  },
  {
    label: 'WPA/WPA2-Personal',
    value: 'WPA/WPA2-PERSONAL',
    bandSupportList: ['2', '5'],
    isShow: true,
  },
  {
    label: 'WPA2-Personal',
    value: 'WPA2-PERSONAL',
    bandSupportList: ['2', '5'],
    isShow: true,
  },
  {
    label: 'WPA2/WPA3-Personal',
    value: 'WPA2/WPA3-PERSONAL',
    bandSupportList: ['2', '5'],
    isShow: true,
  },
  {
    label: 'WPA3-Personal',
    value: 'WPA3-PERSONAL',
    bandSupportList: ['2', '5', '6'],
    isShow: true,
  },
  {
    label: 'WPA/WPA2-Enterprise',
    value: 'WPA/WPA2-ENTERPRISE',
    bandSupportList: ['2', '5'],
    isShow: true,
  },
  {
    label: 'WPA2-Enterprise',
    value: 'WPA2-ENTERPRISE',
    bandSupportList: ['2', '5'],
    isShow: true,
  },
  {
    label: 'WPA3-Enterprise',
    value: 'WPA3-ENTERPRISE',
    bandSupportList: ['2', '5', '6'],
    isShow: true,
  },
];

export const securityTypeConfig = {
  OPEN: {
    wpaMode: { display: '', value: '' },
    encryption: '',
    isPasswordRequired: false,
    isRadiusProfileRequired: false,
  },
  ENHANCE_OPEN: {
    wpaMode: { display: '', value: '' },
    encryption: '',
    isPasswordRequired: false,
    isRadiusProfileRequired: false,
  },
  'ENHANCE_OPEN+OPEN': {
    wpaMode: { display: '', value: '' },
    encryption: '',
    isPasswordRequired: false,
    isRadiusProfileRequired: false,
  },
  'WPA/WPA2-PERSONAL': {
    wpaMode: { display: 'WPA/WPA2', value: 'WPA/WPA2' },
    encryption: 'PSK-TKIP/AES',
    isPasswordRequired: true,
    isRadiusProfileRequired: false,
  },
  'WPA2-PERSONAL': {
    wpaMode: { display: 'WPA2-Only', value: 'WPA2' },
    encryption: 'PSK-AES',
    isPasswordRequired: true,
    isRadiusProfileRequired: false,
  },
  'WPA2/WPA3-PERSONAL': {
    wpaMode: { display: 'WPA2/WPA3', value: 'WPA2/WPA3' },
    encryption: 'PSK/SAE-AES',
    isPasswordRequired: true,
    isRadiusProfileRequired: false,
  },
  'WPA3-PERSONAL': {
    wpaMode: { display: 'WPA3-Only', value: 'WPA3' },
    encryption: 'SAE-AES',
    isPasswordRequired: true,
    isRadiusProfileRequired: false,
  },
  'WPA/WPA2-ENTERPRISE': {
    wpaMode: { display: 'WPA/WPA2', value: 'WPA/WPA2' },
    encryption: 'PSK-TKIP/AES',
    isPasswordRequired: false,
    isRadiusProfileRequired: true,
  },
  'WPA2-ENTERPRISE': {
    wpaMode: { display: 'WPA2-Only', value: 'WPA2' },
    encryption: 'PSK-AES',
    isPasswordRequired: false,
    isRadiusProfileRequired: true,
  },
  'WPA3-ENTERPRISE': {
    wpaMode: { display: 'WPA3-Only', value: 'WPA3' },
    encryption: 'SAE-AES',
    isPasswordRequired: false,
    isRadiusProfileRequired: true,
  },
};

export const SECURITY_ALLOWED_6G = [
  'ENHANCE_OPEN',
  'WPA3-PERSONAL',
  'WPA3-ENTERPRISE',
];

export const SECURITY_ALLOWED_MLO = [
  'ENHANCE_OPEN',
  'WPA3-PERSONAL',
  'WPA3-ENTERPRISE',
];

export const HIDE_GROUP_KEY_SECURITY = [
  'OPEN',
  'ENHANCE_OPEN',
  'ENHANCE_OPEN+OPEN',
];

export const isGroupKeyVisible = (security) => {
  return !HIDE_GROUP_KEY_SECURITY.includes(security);
};

export const PMF_MAP = {
  'WPA/WPA2': 'Capable',
  'WPA2-Only': 'Capable',
  'WPA2/WPA3': 'Capable',
  'WPA3-Only': 'Mandatory',
};

export const formLimitation = {
  vlanId: {
    min: 1,
    max: 4094
  },
  groupKeyUpdatePeriod: {
    min: 300,
    max: 86400
  },
  rateLimitDownload: {
    min: 1,
    max: 2560
  },
  rateLimitUpload: {
    min: 1,
    max: 2560
  }
}